import React, { useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CloseCircleOutlined,
  CloudUploadOutlined,
  EditOutlined,
  StopOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { fmtDate, stageLabel } from './shared/recruitUi';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { buildLinearWorkflow, hrmsStatusColor } from '../shared/hrmsStatus';

const { Text, Paragraph } = Typography;

const JobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<string | null>(null);

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.job(id || ''),
    queryFn: () => hrmsService.getRecruitJob(id!),
    enabled: Boolean(id),
  });

  const { data: careers } = useQuery({
    queryKey: hrmsKeys.careersSettings(),
    queryFn: () => hrmsService.getCareersSettings(),
  });

  const workflow = useMemo(
    () => buildLinearWorkflow(['Draft', 'Published', 'Closed'], row?.status || 'Draft'),
    [row?.status]
  );

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.job(id!) });
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.jobs() });
  };

  const run = async (key: string, fn: () => Promise<any>, okMsg: string) => {
    setLoading(key);
    try {
      await fn();
      message.success(okMsg);
      await invalidate();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Action failed');
    } finally {
      setLoading(null);
    }
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Job" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Job" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const careersSlug = careers?.careers_slug;
  const publicUrl =
    careersSlug && row.slug
      ? `${window.location.origin}/careers/${careersSlug}/jobs/${row.slug}`
      : null;
  const embedSnippet = publicUrl
    ? `<iframe src="${publicUrl}" width="100%" height="600" frameborder="0" title="${row.title}"></iframe>`
    : null;

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Job posting"
          documentNumber={row.job_number || '—'}
          statusTag={<Tag color={hrmsStatusColor(row.status)}>{row.status}</Tag>}
          subtitle={<Text type="secondary">{row.title}</Text>}
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/jobs')}>
                Back
              </Button>
              {row.status !== 'Closed' && (
                <Button icon={<EditOutlined />} onClick={() => navigate(`/hr/jobs/${id}/edit`)}>
                  Edit
                </Button>
              )}
              <Button icon={<TeamOutlined />} onClick={() => navigate(`/hr/applications?view=board&job_posting_id=${id}`)}>
                Pipeline
              </Button>
              {row.status !== 'Published' && row.status !== 'Closed' && (
                <Button
                  type="primary"
                  icon={<CloudUploadOutlined />}
                  loading={loading === 'publish'}
                  onClick={() => run('publish', () => hrmsService.publishJob(id!), 'Published')}
                >
                  Publish
                </Button>
              )}
              {row.status === 'Published' && (
                <Button
                  icon={<StopOutlined />}
                  loading={loading === 'unpublish'}
                  onClick={() =>
                    run('unpublish', () => hrmsService.unpublishJob(id!), 'Unpublished')
                  }
                >
                  Unpublish
                </Button>
              )}
              {row.status !== 'Closed' && (
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  loading={loading === 'close'}
                  onClick={() => run('close', () => hrmsService.closeJob(id!), 'Closed')}
                >
                  Close
                </Button>
              )}
            </Space>
          }
        />
      }
      workflow={<WorkflowStrip stages={workflow.stages} current={workflow.current} compact />}
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card title="Summary" style={{ marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Title">{row.title}</Descriptions.Item>
              <Descriptions.Item label="Slug">{row.slug}</Descriptions.Item>
              <Descriptions.Item label="Channel">{row.channel}</Descriptions.Item>
              <Descriptions.Item label="Employment type">
                {row.employment_type || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Openings">{row.openings}</Descriptions.Item>
              <Descriptions.Item label="Filled">{row.filled_count ?? 0}</Descriptions.Item>
              <Descriptions.Item label="Location">{row.location_text || '—'}</Descriptions.Item>
              <Descriptions.Item label="Closing date">{fmtDate(row.closing_date)}</Descriptions.Item>
              <Descriptions.Item label="Department">{row.department_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Requisition">
                {row.requisition_id ? (
                  <a onClick={() => navigate(`/hr/requisitions/${row.requisition_id}`)}>{row.requisition_number}</a>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Recruiter">{row.recruiter_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Hiring manager">{row.hiring_manager_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Salary">
                {row.salary_min != null || row.salary_max != null
                  ? `${row.currency || 'AED'} ${row.salary_min ?? '—'} – ${row.salary_max ?? '—'} (${row.salary_visibility})`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {row.description || '—'}
                </Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="Requirements" span={2}>
                <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {row.requirements || '—'}
                </Paragraph>
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <EntityAttachmentsCard
            entityType="HR_JOB"
            entityId={row.id}
            title="Job documents"
            uploadHint="JD PDF, org chart, branding assets, interview guide"
          />
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Pipeline" size="small" style={{ marginBottom: 16 }}>
            <Space wrap>
              {['Applied', 'Screening', 'Interview', 'Offer', 'Hired', 'TalentPool', 'Rejected'].map((s) => (
                <Tag
                  key={s}
                  color={hrmsStatusColor(s)}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/hr/applications?job_posting_id=${id}&status=${s}`)}
                >
                  {stageLabel(s)}: {row.stage_counts?.[s] || 0}
                </Tag>
              ))}
            </Space>
          </Card>
          {Array.isArray(row.questions_json) && row.questions_json.length > 0 && (
            <Card title="Screening questions" size="small" style={{ marginBottom: 16 }}>
              <ol style={{ paddingLeft: 18, margin: 0 }}>
                {row.questions_json.map((q: any) => (
                  <li key={q.code}>
                    {q.text} <Text type="secondary">({q.type}{q.required ? ', required' : ''})</Text>
                  </li>
                ))}
              </ol>
            </Card>
          )}
          {row.status === 'Published' && publicUrl ? (
            <Card title="Public careers link" style={{ marginBottom: 16 }}>
              <Alert
                type="info"
                showIcon
                message="Published"
                description={
                  <a href={publicUrl} target="_blank" rel="noreferrer">
                    {publicUrl}
                  </a>
                }
                style={{ marginBottom: 12 }}
              />
              <Text type="secondary">Embed snippet</Text>
              <Input.TextArea value={embedSnippet || ''} readOnly rows={4} style={{ marginTop: 8 }} />
            </Card>
          ) : (
            <Card title="Public careers link">
              <Text type="secondary">Publish the job to generate a public careers URL.</Text>
            </Card>
          )}
        </Col>
      </Row>
    </WorkspaceLayout>
  );
};

export default JobDetailPage;
